import {
  Button,
  ButtonProps,
  Flex,
  IconProps,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import { ComponentType, forwardRef } from "react";
import { usePopoverOpenState } from "../../utils/usePopoverOpenState";
import { CaretDownIcon } from "../icons/CaretDownIcon";
import { CaretUpIcon } from "../icons/CaretUpIcon";

export type SortDirection = "asc" | "desc";

export interface SortOption {
  id: string;
  name: string;
  Icon: ComponentType<IconProps>;
  defaultDirection: SortDirection;
}

interface ReadingListSortMenuProps {
  sortKey: string;
  sortDirection: SortDirection;
  options: SortOption[];
  onChange: (key: string, direction: SortDirection) => void;
  ariaLabel: string;
}

export function ReadingListSortMenu({
  sortKey,
  sortDirection,
  options,
  onChange,
  ariaLabel,
}: ReadingListSortMenuProps) {
  const { ref, onToggle, isOpen, onClose } = usePopoverOpenState();
  const active = options.find((o) => o.id === sortKey) ?? options[0];
  const ArrowIcon = sortDirection === "asc" ? CaretUpIcon : CaretDownIcon;

  return (
    <Popover variant="filterSmall" gutter={4} isOpen={isOpen} isLazy>
      <PopoverTrigger>
        <SortMenuTrigger
          aria-label={ariaLabel}
          isActive={isOpen}
          style={{ pointerEvents: isOpen ? "none" : "auto" }}
          onMouseUp={onToggle}
          activeIcon={<active.Icon w="24px" h="24px" color="neutral.200" />}
          activeLabel={active.name}
          arrowIcon={<ArrowIcon w="24px" h="24px" color="neutral.200" />}
        />
      </PopoverTrigger>
      <PopoverContent
        ref={ref}
        variants={{
          enter: { opacity: 1, top: 0 },
          exit: { opacity: 0, top: -20 },
        }}
      >
        <PopoverBody sx={{ p: "6px" }}>
          <Flex direction="column" gap="2px">
            {options.map((opt) => {
              const isActiveRow = opt.id === sortKey;
              return (
                <Button
                  key={opt.id}
                  variant="ghost"
                  size="sm"
                  width="100%"
                  height="32px"
                  px="10px"
                  justifyContent="space-between"
                  bg={isActiveRow ? "transparent.white.10" : undefined}
                  color="dune.dune"
                  _hover={{ bg: "transparent.white.10" }}
                  onClick={() => {
                    if (isActiveRow) {
                      onChange(
                        opt.id,
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      onChange(opt.id, opt.defaultDirection);
                    }
                    onClose();
                  }}
                >
                  <Flex align="center" gap="10px">
                    <opt.Icon w="14px" h="14px" color="dune.100" />
                    <Text variant="filterTag">{opt.name}</Text>
                  </Flex>
                  {isActiveRow && (
                    <ArrowIcon w="12px" h="12px" color="dune.100" />
                  )}
                </Button>
              );
            })}
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

interface TriggerProps extends Omit<ButtonProps, "children"> {
  activeIcon: React.ReactNode;
  activeLabel: string;
  arrowIcon: React.ReactNode;
}

const SortMenuTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ activeIcon, activeLabel, arrowIcon, ...props }, ref) => {
    return (
      <Button
        variant="sortMenu"
        size="md"
        height="36px"
        minW="auto"
        // Slightly more horizontal padding now that the trigger carries
        // a label between the leading icon and the arrow — keeps the
        // breathing room between the inner content and the rounded edges.
        px="12px"
        ref={ref}
        gap="8px"
        {...props}
      >
        {activeIcon}
        <Text variant="filterTag" color="neutral.200">
          {activeLabel}
        </Text>
        {arrowIcon}
      </Button>
    );
  },
);
